// Versus network session (client side of server/src/index.js).
//
//   findMatch() → Lobby socket (/find) → 'matched' → Match socket (/match/<id>)
//   → 'round' / 'drop' / 'taken' / 'round_end' / 'opponent_left' from the server,
//   'state' / 'shot' / 'hit' / 'fx' relayed from the opponent.
//
// The session outlives scenes (VersusScene restarts every round), so it is a module
// singleton. Listeners are per message type; scenes remove theirs on shutdown.

import type { MechId } from '../core/types';
import type { VisNode } from '../systems/versus/mirror';

/** Server URL: ?mp=… (testing) → VITE_MP_URL (build) → the deployed worker. */
function serverUrl(): string {
  try {
    const q = new URLSearchParams(window.location.search).get('mp');
    if (q) return q.replace(/\/$/, '');
  } catch {
    // no window
  }
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_MP_URL;
  return (env || DEFAULT_SERVER).replace(/\/$/, '');
}

/** Deployed versus worker (npm run deploy:server). */
const DEFAULT_SERVER = 'wss://neon-core-versus.vengeance77.workers.dev';

export type VersusStatus = 'idle' | 'connecting' | 'queued' | 'matched' | 'playing' | 'over' | 'error';

export interface Opponent {
  name: string;
  mech: MechId;
}

export interface RoundMsg {
  t: 'round';
  round: number;
  score: [number, number];
  arena: number;
  sector: number;
  seed: number;
  startsIn: number;
  players: ({ name: string; mech: MechId } | null)[];
}

export interface DropMsg {
  t: 'drop';
  id: number;
  kind: 'weapon' | 'health' | 'item';
  spot: number;
  pick: number;
}

/** Remote player snapshot (≈ 20 Hz). */
export interface StateMsg {
  t: 'state';
  x: number;
  /** Feet y. */
  y: number;
  vx: number;
  vy: number;
  f: 1 | -1;
  aim: number;
  /** Current view animation, frame and frame count. */
  anim: string;
  fr: number;
  fc: number;
  spin: number;
  tuck: number;
  hp: number;
  maxHp: number;
  sh: number;
  maxSh: number;
  alive: boolean;
  weapon: string | null;
  equip?: string[];
  dash: boolean;
  /** Round the snapshot belongs to (late snapshots from a previous round are ignored). */
  r?: number;
  /** Ability visuals (see systems/versus/mirror.ts). */
  vis?: VisNode[];
}

export interface HitMsg {
  t: 'hit';
  /** Final damage after the shooter's multipliers. */
  a: number;
  tags: string[];
  sx?: number;
  sy?: number;
  kb?: number;
  crit?: boolean;
  dot?: boolean;
  label?: string;
}

type Msg = { t: string; [k: string]: unknown };
type Handler = (m: Msg) => void;

class VersusSession {
  status: VersusStatus = 'idle';
  side: 0 | 1 = 0;
  opponent: Opponent | null = null;
  score: [number, number] = [0, 0];
  lastRound: RoundMsg | null = null;
  /** performance.now() when the current round message arrived (the countdown runs on real time). */
  roundAt = 0;
  /** Drops of the current round not yet taken (a scene that loads late replays them). */
  readonly activeDrops = new Map<number, DropMsg>();
  error = '';
  name = 'PILOT';
  mech: MechId = 'volt';

  private lobby: WebSocket | null = null;
  private match: WebSocket | null = null;
  private handlers = new Map<string, Set<Handler>>();
  private ping: ReturnType<typeof setInterval> | null = null;

  on(type: string, fn: Handler): () => void {
    let set = this.handlers.get(type);
    if (!set) this.handlers.set(type, (set = new Set()));
    set.add(fn);
    return () => set!.delete(fn);
  }

  private emit(m: Msg): void {
    for (const fn of [...(this.handlers.get(m.t) ?? [])]) {
      try {
        fn(m);
      } catch (err) {
        console.warn(`[versus] handler for ${m.t} failed`, err);
      }
    }
    for (const fn of [...(this.handlers.get('*') ?? [])]) fn(m);
  }

  private setStatus(s: VersusStatus, error = ''): void {
    this.status = s;
    this.error = error;
    this.emit({ t: 'status', status: s, error });
  }

  findMatch(name: string, mech: MechId): void {
    this.leave();
    this.name = name;
    this.mech = mech;
    this.score = [0, 0];
    this.lastRound = null;
    this.opponent = null;
    this.setStatus('connecting');
    let ws: WebSocket;
    try {
      ws = new WebSocket(`${serverUrl()}/find?name=${encodeURIComponent(name)}&mech=${mech}`);
    } catch {
      this.setStatus('error', 'Could not reach the versus server.');
      return;
    }
    this.lobby = ws;
    ws.onmessage = (e) => {
      const m = parse(e.data);
      if (!m) return;
      if (m.t === 'queued') this.setStatus('queued');
      if (m.t === 'matched') {
        this.side = m.side === 1 ? 1 : 0;
        this.opponent = m.opponent as Opponent;
        this.lobby = null;
        ws.onclose = null;
        try {
          ws.close();
        } catch {
          // already closing
        }
        this.setStatus('matched');
        this.joinMatch(String(m.matchId));
      }
    };
    ws.onerror = () => this.setStatus('error', 'Could not reach the versus server.');
    ws.onclose = () => {
      if (this.lobby === ws && this.status !== 'error') this.setStatus('error', 'Lost connection to the matchmaker.');
    };
    this.startPing();
  }

  private joinMatch(id: string): void {
    const ws = new WebSocket(`${serverUrl()}/match/${id}?side=${this.side}&name=${encodeURIComponent(this.name)}&mech=${this.mech}`);
    this.match = ws;
    ws.onmessage = (e) => {
      const m = parse(e.data);
      if (!m) return;
      if (m.t === 'round') {
        this.lastRound = m as unknown as RoundMsg;
        this.roundAt = performance.now();
        this.activeDrops.clear();
        this.score = [...(m.score as [number, number])] as [number, number];
        if (this.status !== 'playing') this.setStatus('playing');
      }
      if (m.t === 'round_end') {
        this.score = [...(m.score as [number, number])] as [number, number];
        if (m.over) this.status = 'over';
      }
      if (m.t === 'opponent_left') this.status = 'over';
      if (m.t === 'drop') this.activeDrops.set(Number(m.id), m as unknown as DropMsg);
      if (m.t === 'taken') this.activeDrops.delete(Number(m.id));
      this.emit(m);
    };
    ws.onerror = () => {
      if (this.match === ws && this.status !== 'over') this.setStatus('error', 'Connection to the match failed.');
    };
    ws.onclose = () => {
      if (this.match !== ws) return;
      this.match = null;
      if (this.status !== 'over' && this.status !== 'idle') {
        this.status = 'over';
        this.emit({ t: 'disconnected' });
      }
    };
  }

  private startPing(): void {
    if (this.ping) clearInterval(this.ping);
    this.ping = setInterval(() => {
      const ws = this.match ?? this.lobby;
      if (ws && ws.readyState === WebSocket.OPEN) ws.send('ping');
    }, 15000);
  }

  /** Send to the match (relayed to the opponent or handled by the server). */
  send(msg: Msg): void {
    if (this.local) {
      this.local.fromPlayer(msg);
      return;
    }
    const ws = this.match;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
  }

  get connected(): boolean {
    return !!this.match && this.match.readyState === WebSocket.OPEN;
  }

  /** Cancel the search / forfeit the match and drop every socket. */
  /** 'online' (matchmaking server) or 'ai' (offline duel against a bot, see LocalMatch). */
  mode: 'online' | 'ai' = 'online';
  /** AI difficulty (0 easy · 1 normal · 2 hard) of the current offline match. */
  aiLevel = 1;
  private local: LocalMatch | null = null;

  /** Offline duel against an AI pilot: same rounds, drops and scoring, no network. */
  startAI(name: string, mech: MechId, botMech: MechId, level: number): void {
    this.leave();
    this.mode = 'ai';
    this.aiLevel = level;
    this.name = name;
    this.mech = mech;
    this.side = 0;
    this.score = [0, 0];
    this.lastRound = null;
    this.activeDrops.clear();
    this.opponent = { name: `AI · ${['EASY', 'NORMAL', 'HARD'][level] ?? 'NORMAL'}`, mech: botMech };
    this.local = new LocalMatch(
      [
        { name, mech },
        { name: this.opponent.name, mech: botMech },
      ],
      (m) => this.deliver(m),
    );
    this.setStatus('matched');
    this.local.start();
  }

  /** The AI pilot (side 1) reports its own death / drop claims. */
  fromBot(msg: Msg): void {
    this.local?.fromSide(1, msg);
  }

  /** Same bookkeeping as a server message, then to listeners. */
  private deliver(m: Msg): void {
    if (m.t === 'round') {
      this.lastRound = m as unknown as RoundMsg;
      this.roundAt = performance.now();
      this.activeDrops.clear();
      this.score = [...(m.score as [number, number])] as [number, number];
      if (this.status !== 'playing') this.setStatus('playing');
    }
    if (m.t === 'round_end') {
      this.score = [...(m.score as [number, number])] as [number, number];
      if (m.over) this.status = 'over';
    }
    if (m.t === 'drop') this.activeDrops.set(Number(m.id), m as unknown as DropMsg);
    if (m.t === 'taken') this.activeDrops.delete(Number(m.id));
    this.emit(m);
  }

  leave(): void {
    this.local?.stop();
    this.local = null;
    this.mode = 'online';
    for (const ws of [this.lobby, this.match]) {
      if (!ws) continue;
      ws.onclose = null;
      ws.onmessage = null;
      try {
        ws.close(1000, 'leave');
      } catch {
        // ignore
      }
    }
    this.lobby = null;
    this.match = null;
    if (this.ping) clearInterval(this.ping);
    this.ping = null;
    this.status = 'idle';
  }
}

function parse(data: unknown): Msg | null {
  if (typeof data !== 'string') return null;
  try {
    const m = JSON.parse(data) as Msg;
    return m && typeof m.t === 'string' ? m : null;
  } catch {
    return null;
  }
}

export const versus = new VersusSession();

// ───────────────────────────── Offline match (VS AI) ─────────────────────────────

const L = { WIN: 3, ARENAS: 2, SECTORS: 3, ROUND_DELAY: 3200, INTERMISSION: 2600, DROP_FIRST: 4500, DROP_MIN: 5500, DROP_MAX: 9000, DROP_MAX_ACTIVE: 4, DROP_SPOTS: 64 };
const rnd = (a: number, b: number): number => a + Math.floor(Math.random() * (b - a + 1));

/** In-process copy of the server's Match rules (server/src/index.js) for offline duels. */
class LocalMatch {
  private score: [number, number] = [0, 0];
  private round = 0;
  private phase: 'countdown' | 'fighting' | 'intermission' | 'over' = 'countdown';
  private drops = new Set<number>();
  private nextDrop = 1;
  private timers = new Set<ReturnType<typeof setTimeout>>();

  constructor(
    private readonly players: { name: string; mech: MechId }[],
    private readonly out: (m: Msg) => void,
  ) {}

  private later(ms: number, fn: () => void): void {
    const id = setTimeout(() => {
      this.timers.delete(id);
      fn();
    }, ms);
    this.timers.add(id);
  }

  start(): void {
    this.startRound();
  }

  stop(): void {
    for (const id of this.timers) clearTimeout(id);
    this.timers.clear();
    this.phase = 'over';
  }

  /** The local player (side 0). */
  fromPlayer(m: Msg): void {
    this.fromSide(0, m);
  }

  fromSide(side: 0 | 1, m: Msg): void {
    if (m.t === 'dead' && this.phase === 'fighting' && m.round === this.round) this.endRound(side === 0 ? 1 : 0);
    if (m.t === 'claim' && this.phase === 'fighting' && this.drops.delete(Number(m.id))) this.out({ t: 'taken', id: m.id, by: side });
  }

  private startRound(): void {
    this.round++;
    this.phase = 'countdown';
    this.drops.clear();
    this.out({
      t: 'round',
      round: this.round,
      score: [...this.score],
      arena: rnd(0, L.ARENAS - 1),
      sector: rnd(0, L.SECTORS - 1),
      seed: rnd(1, 2 ** 30),
      startsIn: L.ROUND_DELAY,
      players: this.players,
    });
    this.later(L.ROUND_DELAY, () => {
      if (this.phase !== 'countdown') return;
      this.phase = 'fighting';
      this.later(L.DROP_FIRST, () => this.spawnDrop());
    });
  }

  private spawnDrop(): void {
    if (this.phase !== 'fighting') return;
    if (this.drops.size < L.DROP_MAX_ACTIVE) {
      const r = Math.random();
      const id = this.nextDrop++;
      this.drops.add(id);
      this.out({ t: 'drop', id, kind: r < 0.38 ? 'weapon' : r < 0.7 ? 'health' : 'item', spot: rnd(0, L.DROP_SPOTS - 1), pick: rnd(0, 2 ** 30) });
    }
    this.later(rnd(L.DROP_MIN, L.DROP_MAX), () => this.spawnDrop());
  }

  private endRound(winner: 0 | 1): void {
    for (const id of this.timers) clearTimeout(id);
    this.timers.clear();
    this.score[winner]++;
    const over = this.score[winner] >= L.WIN;
    this.phase = over ? 'over' : 'intermission';
    this.out({ t: 'round_end', round: this.round, winner, score: [...this.score], over });
    if (!over) this.later(L.INTERMISSION, () => this.startRound());
  }
}
